/* eslint-disable no-nested-ternary */
import { Box } from "@chakra-ui/react"
import { OpenGatePricing } from "basket_and_checkout/shared/components/PDP/Pricing/OpenGatePricingContainer"
import { promoCodeExperimentValues } from "components/Basket/components/PromoCodes/constants"
import { ExperimentFlags } from "@shared/utils.experimentation"
import { useFeatureFlag, useHasCurrentMembership } from "hooks"
import { useScreenViewport } from "lib/checkViewport"
import { Logger } from "lib/logger"
import { AddPromoCodeBar } from "packages/acquisition/components/PromoCodes/AddPromoCodeBar/AddPromoCodeBar"
import { AddPromoCodeBox } from "packages/acquisition/components/PromoCodes/AddPromoCodeBox/AddPromoCodeBox"
import { verticalMargin } from "packages/acquisition/components/PromoCodes/AddPromoCodeBox/AddPromoCodeBox.css"
import React from "react"
import { ErrorBoundary } from "react-error-boundary"
import type {
  MembershipOptionsV2,
  PricingToggle,
  ShippingType,
  VariantType,
} from "types"
import { useHasMounted } from "utils/wrappers"

import { Banner } from "../PDPHeader/Banner"
import { CoreProductInfo } from "../PDPHeader/CoreProductInfo"
import { TextileSizes } from "../PDPHeader/CoreProductInfo/TextileSizes"
import { PDPHeaderFallback } from "../PDPHeader/PDPHeaderFallback"
import { InfoBarLayout, ProductInfoLayout } from "../PDPHeader/PDPHeaderLayout"
import type { PurchaseType } from "../types"
import { ProductOOS } from "../OOS"

import { useSidebarTopOffset } from "./useSidebarTopOffset"

type Props = {
  variant: VariantType
  setConfirmation: React.Dispatch<React.SetStateAction<boolean>>
  purchaseType: PurchaseType
  setPurchaseType: React.Dispatch<React.SetStateAction<PurchaseType>>
  isModalOpen: boolean
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  shippingInfo?: ShippingType[]
  pricingToggle?: PricingToggle
  isOpenGate: boolean
  pdpMembershipOptions?: MembershipOptionsV2[]
  membershipInBag?: boolean
}

export const PDPHeaderV2: React.FC<Props> = ({
  variant,
  setConfirmation,
  purchaseType,
  setPurchaseType,
  setIsModalOpen,
  isModalOpen,
  shippingInfo,
  pricingToggle,
  isOpenGate,
  pdpMembershipOptions,
  membershipInBag,
}) => {
  const hasMounted = useHasMounted()
  const outOfStock = variant?.availability?.status === "outofstock"
  const isMember = useHasCurrentMembership()
  const [promoCodeLoading, promoFlagExperimentValue] = useFeatureFlag(
    ExperimentFlags.USE_PROMO_CODE_EXPERIMENT,
    promoCodeExperimentValues.throwaway
  )

  const { isMobile } = useScreenViewport()
  const { sidebarOffsetTop } = useSidebarTopOffset(hasMounted, isMobile)

  const hasInfo = !!variant?.infoBanner?.length

  const isSize = variant?.type === "size"
  const canShowPromoCode = !isMember && !promoCodeLoading

  const showPromoCodeBar =
    canShowPromoCode &&
    promoFlagExperimentValue === promoCodeExperimentValues.promo_bar

  const showPromoCodeBox =
    canShowPromoCode &&
    promoFlagExperimentValue === promoCodeExperimentValues.promo_box

  const canRenderPricingComponent = !outOfStock && (hasMounted || isOpenGate)

  return (
    <Box
      id="pdp-sidebar"
      position={{
        lg: "sticky",
      }}
      top={{
        lg: `${sidebarOffsetTop}px`,
      }}
      transition={{
        lg: "top 0.3s",
      }}
    >
      <Box pt={{ base: 0, md: 2, lg: hasInfo ? 2 : 7 }} pb={{ base: 0, lg: 3 }}>
        <ErrorBoundary
          FallbackComponent={() => (
            <Box textAlign="center" pt={{ base: 1, md: 0 }}>
              <PDPHeaderFallback />
            </Box>
          )}
          onError={(error) =>
            Logger.warn(`PDP product info failed to load. ${error}`)
          }
        >
          <InfoBarLayout>
            {!!variant.infoBanner && <Banner text={variant.infoBanner} />}
          </InfoBarLayout>

          <ProductInfoLayout hasInfo={hasInfo}>
            {/* render client side to avoid hydration issues */}
            {isMobile && showPromoCodeBar && (
              <AddPromoCodeBar showPromoText useBgPromoText />
            )}
            {isMobile && showPromoCodeBox && (
              <AddPromoCodeBox className={verticalMargin} />
            )}
            <CoreProductInfo
              variant={variant}
              setIsModalOpen={setIsModalOpen}
              isModalOpen={isModalOpen}
            />
            {!isMobile && showPromoCodeBox && (
              <AddPromoCodeBox className={verticalMargin} />
            )}
            {!isMobile && showPromoCodeBar && (
              <AddPromoCodeBar showPromoText useBgPromoText />
            )}
            {outOfStock && <ProductOOS variant={variant} />}
            {canRenderPricingComponent ? (
              <OpenGatePricing
                variant={variant}
                setConfirmation={setConfirmation}
                purchaseType={purchaseType}
                setPurchaseType={setPurchaseType}
                shippingInfo={shippingInfo}
                pricingToggle={pricingToggle as PricingToggle}
                pdpMembershipOptions={pdpMembershipOptions}
                membershipInBag={!!membershipInBag}
              />
            ) : (
              <Box w="full" h="1rem" /> // placeholder
            )}
          </ProductInfoLayout>

          {isSize && (
            <ProductInfoLayout hasInfo={hasInfo}>
              <TextileSizes variant={variant} />
            </ProductInfoLayout>
          )}
        </ErrorBoundary>
      </Box>
    </Box>
  )
}
